import { DTX_APP_ID, DTX_PUBLISHER_ID } from '@moment/layouts/Ads/constants.ts';
import { BannerAdSizes } from '@moment/components/GalleryAd/ad-constants.ts';

export const PUBWISE_SCRIPT_ATTR = 'data-pubwise';

const appendScript = (src: string, attrs: Record<string, string> = {}) =>
  new Promise<void>((resolve, reject) => {
    const scriptEl = document.createElement('script');
    scriptEl.src = src;
    Object.entries(attrs).forEach(([k, v]) => scriptEl.setAttribute(k, v));
    scriptEl.onload = () => resolve();
    scriptEl.onerror = reject;
    document.body.appendChild(scriptEl);
  });

const onceLoads = new Map<string, Promise<void>>();

// Loads a script at most once per page. Failures are evicted so a later call can retry.
const loadScriptOnce = (src: string): Promise<void> => {
  const existing = onceLoads.get(src);
  if (existing) {
    return existing;
  }
  const pending = appendScript(src);
  onceLoads.set(src, pending);
  pending.catch(() => onceLoads.delete(src));
  return pending;
};

export const loadAllAdScripts = async (scripts: (string | undefined)[]) =>
  Promise.all(scripts.filter((it): it is string => !!it).map(loadScriptOnce));

// Pubwise must be re-loadable (the config can change mid-session), so it is exempt from dedupe.
// Nodes are tagged so tearDownPubwise can remove them.
export const loadPubwiseScripts = async (scripts: (string | undefined)[]) =>
  Promise.all(
    scripts
      .filter((it): it is string => !!it)
      .map((src) => appendScript(src, { [PUBWISE_SCRIPT_ATTR]: 'true' }))
  );

// The pubwise bundle is a singleton per document (it installs globals and, once Prebid is loaded,
// a second copy aborts), so switching bundles must be strictly sequential:
//  - never two bundles in flight (a <script> that was already requested still executes when it
//    arrives, so tearing down while one is in flight would let it clobber the next bundle);
//  - the identity of a bundle is its script URLs, not the {brandGroup, utm} key: a key change that
//    resolves to the same bundle needs no work at all.
let pubwiseQueue: Promise<unknown> = Promise.resolve();
let pubwiseAttempted: string | null = null;
let pubwiseOk = false;

interface SwitchPubwiseHooks {
  // Checked when this request's turn comes: a request that was superseded in the meantime is skipped.
  isCurrent: () => boolean;
  // Resets what the previous bundle left behind (see tearDownPubwise).
  teardown: () => void;
  // Runs right before every bundle load (including the first one).
  beforeLoad?: () => void;
}

/** Resolves true when the bundle for `scripts` is loaded, false when the request was skipped. */
export const switchPubwiseBundle = (
  scripts: (string | undefined)[],
  { isCurrent, teardown, beforeLoad }: SwitchPubwiseHooks
): Promise<boolean> => {
  const run = pubwiseQueue.then(async () => {
    if (!isCurrent()) {
      return false;
    }
    const signature = scripts.filter(Boolean).join('|');
    if (signature === pubwiseAttempted && pubwiseOk) {
      return true;
    }
    if (pubwiseAttempted !== null) {
      // something from a previous (or failed) bundle may be on the page
      teardown();
    }
    pubwiseAttempted = signature;
    pubwiseOk = false;
    beforeLoad?.();
    await loadPubwiseScripts(scripts);
    pubwiseOk = true;
    return true;
  });
  pubwiseQueue = run.catch(() => undefined);
  return run;
};

// Test hook: forget which scripts were loaded.
export const resetAdScriptLoaderForTests = () => {
  onceLoads.clear();
  pubwiseQueue = Promise.resolve();
  pubwiseAttempted = null;
  pubwiseOk = false;
};

export const loadAdScripts = async () => {
  const list = ['/assets/scripts/dtx-web-sdk.umd.cjs'];
  return loadAllAdScripts(list);
};

interface EventPayload {
  eventName: string;
  callback: (data: any) => void; // Adjust 'any' to a specific type if known
}

export const initializeAds = (
  listeners: EventPayload[],
  appBundle?: string,
  appName?: string,
  appVersion?: string,
  advId?: string,
  userId?: string
) => {
  let hostingAppDetails = undefined;
  if (appBundle && appName && appVersion) {
    hostingAppDetails = {
      packageName: appBundle,
      appVersion: appVersion,
      appName: appName,
    };
  }

  try {
    window.DTXWeb.getInstance().initialize({
      appId: DTX_APP_ID,
      hostingAppDetails: hostingAppDetails,
      advertiserId: advId,
      advertiserIdType: 'android',
      publisherId: DTX_PUBLISHER_ID,
      siteId: window?.location?.hostname,
      userId: userId,
    });
    listeners.forEach((l) => {
      window.DTXWeb.getInstance().addEventListener(l.eventName, l.callback);
    });
  } catch (e) {
    console.log(`Error initializing SDK`, e);
  }
};

export interface AdRequest {
  bannerConfig: BannerAdSizes;
  deviceLanguage: string;
  iabCategories?: string[];
  testData?: object;
}

export interface LoadAdRequest {
  success: boolean;
  error?: Error;
}

export const renderAd = async (
  containerId: string,
  tagId: string,
  bannerSizes: BannerAdSizes[],
  lang: string,
  iabCategories?: string[],
  testData?: any
): Promise<boolean> => {
  let ad: undefined;
  try {
    const bannerAd = window.DTXWeb.getInstance().banner(tagId);
    const adRequest: AdRequest = {
      bannerSizes: bannerSizes.map((b) => b.toDtxSizing()),
      deviceLanguage: lang,
      iabCategories: iabCategories,
    };
    if (testData) {
      adRequest.testData = {
        test_data: testData,
      };
    }
    const result: LoadAdRequest = await bannerAd.request(adRequest);
    if (result.success) {
      ad = bannerAd;
      console.log(`Ad ${containerId} loaded`);
      ad.render(containerId);
    } else {
      console.error(`Error loading ad`, result.error);
    }
    return result.success;
  } catch (e) {
    console.log(`Error rendering ad`, e);
  }
  return !!ad;
};
